import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "redux/store";
import { DEMO_STAY_LISTINGS } from "data/listings";
import { StayDataType } from "data/types";
import { Property } from "redux/reducers/Properties/PropertiesSliceTypes";
import { getallPropertieslisting } from "../../../redux/reducers/Properties/PropertiesSlice";
import SectionGridFeaturePlacesV4 from "containers/PageHome/SectionGridFeaturesPlacesV4";
import SectionGridFeaturePlacesSkeleton from "shared/Skelton/SectionGirdSkelton";
import Pagination from "shared/Pagination/Pagination";

// ─── Brand tokens ─────────────────────────────────────────────────────────────
// Primary orange : #F56948   Navy : #1A1A2E
// ─────────────────────────────────────────────────────────────────────────────

type StatusFilter = "all" | "active" | "paused" | "waiting_for_approval" | "approved";
type SortKey = "default" | "price_asc" | "price_desc" | "reviews";

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "paused", label: "Paused" },
  { key: "waiting_for_approval", label: "Pending" },
  { key: "approved", label: "Approved" },
];

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "default", label: "Default" },
  { key: "price_asc", label: "Price: Low → High" },
  { key: "price_desc", label: "Price: High → Low" },
  { key: "reviews", label: "Most Reviewed" },
];

const MyListing: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [allData, setAllData] = useState<StayDataType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [query, setQuery] = useState<string>("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("default");
  const [showSort, setShowSort] = useState(false);

  const DEMO_DATA: StayDataType[] = DEMO_STAY_LISTINGS.filter((_, i) => i < 8);
  const getRandomIndex = () => Math.floor(Math.random() * 8);

  const mergedData = (dataToBeMerged: Property[]) => {
    const newData = dataToBeMerged?.map((apiItem) => {
      const fallback = DEMO_DATA[getRandomIndex()];
      const addressParts = [apiItem?.neighborhood, apiItem?.city, apiItem?.state].filter(Boolean);
      return {
        ...fallback,
        id: apiItem?.id,
        title: apiItem?.title || fallback?.title,
        place_images: apiItem?.place_images,
        address: addressParts.join(", "),
        neighborhood: apiItem?.neighborhood,
        availability_start: apiItem.availability_start || fallback?.date,
        availability_end: apiItem.availability_end || fallback?.date,
        price: apiItem?.price_per_month,
        href: `/listing-stay-detail/${apiItem?.slug}`,
        property_state: apiItem?.property_state || null,
        active: apiItem?.active !== undefined ? apiItem.active : false,
        property_category: apiItem?.property_category.name,
        property_type: apiItem?.property_type?.name,
        city: apiItem?.city,
        country: apiItem?.country,
        state: apiItem?.state,
        slug: apiItem?.slug,
        reviewCount: apiItem?.average?.average_review || 0,
        reviewTotal: apiItem?.average?.count || 0,
        totalBedrooms: apiItem?.total_bedrooms,
      };
    });
    setAllData(newData);
  };

  const fetchData = async (pge: number, silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      const res = await dispatch(getallPropertieslisting({ page: pge, query })).unwrap();
      mergedData(res?.properties);
      setTotalPages(res?.meta?.total_pages || 1);
    } catch (error) {
      console.error("Error fetching properties:", error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchData(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSearch = async () => {
    setIsSearchLoading(true);
    try {
      await fetchData(1);
      setPage(1);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsSearchLoading(false);
    }
  };

  const handleClear = () => setQuery("");

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData(page, true);
  };

  useEffect(() => {
    if (query === "") fetchData(1);
  }, [query]);

  // ─── Client-side filter + sort (applied on top of fetched page) ──────────
  const displayData = useMemo(() => {
    let result = [...allData];

    // Status filter
    if (statusFilter !== "all") {
      result = result.filter((item: any) => {
        if (statusFilter === "active") return item.active === true;
        if (statusFilter === "paused") return item.active === false;
        return item.property_state === statusFilter;
      });
    }

    // Sort
    if (sortKey === "price_asc") result.sort((a, b) => Number(a.price) - Number(b.price));
    if (sortKey === "price_desc") result.sort((a, b) => Number(b.price) - Number(a.price));
    if (sortKey === "reviews") result.sort((a: any, b: any) => (b.reviewTotal || 0) - (a.reviewTotal || 0));

    return result;
  }, [allData, statusFilter, sortKey]);

  // ─── Stats derived from full fetched data ────────────────────────────────
  const stats = useMemo(() => ({
    total: allData.length,
    active: allData.filter((i: any) => i.active).length,
    paused: allData.filter((i: any) => !i.active).length,
    pending: allData.filter((i: any) => i.property_state === "waiting_for_approval").length,
  }), [allData]);

  // ─── Loading ─────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950">
        <div className="container py-10">
          <div className="mb-8 animate-pulse space-y-2">
            <div className="h-7 w-44 bg-neutral-200 dark:bg-neutral-800 rounded-lg" />
            <div className="h-4 w-64 bg-neutral-100 dark:bg-neutral-800/60 rounded-lg" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 animate-pulse bg-neutral-200 dark:bg-neutral-800 rounded-2xl" />
            ))}
          </div>
          <div className="mb-8 animate-pulse h-12 w-full max-w-md bg-neutral-200 dark:bg-neutral-800 rounded-xl" />
          <SectionGridFeaturePlacesSkeleton />
        </div>
      </div>
    );
  }

  const isEmpty = displayData.length === 0;
  const currentSortLabel = SORT_OPTIONS.find(s => s.key === sortKey)?.label ?? "Sort";

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950">
      <div className="container py-10 lg:py-14 space-y-8">

        {/* ── Page header ────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="block w-8 h-0.5 bg-[#F56948] rounded-full" />
              <span className="text-xs font-semibold uppercase tracking-widest text-[#F56948]">
                Dashboard
              </span>
            </div>
            <h1 className="text-2xl lg:text-3xl font-extrabold text-[#1A1A2E] dark:text-white tracking-tight">
              My Properties
            </h1>
            <p className="mt-1 text-sm text-neutral-400 dark:text-neutral-500">
              Manage, pause, and track all your listings in one place.
            </p>
          </div>

          {/* Add new listing CTA */}
          <a
            href="/add-listing-1"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold bg-[#F56948] text-white hover:bg-[#e0522f] transition-all duration-150 shadow-md shadow-[#F56948]/25 self-start sm:self-auto whitespace-nowrap"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            Add New Listing
          </a>
        </div>

        {/* ── Stats bar ──────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            {
              label: "Total Listings",
              value: stats.total,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                </svg>
              ),
              color: "text-[#1A1A2E] dark:text-white",
              bg: "bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800",
              iconBg: "bg-[#1A1A2E]/8 dark:bg-white/10 text-[#1A1A2E] dark:text-white",
            },
            {
              label: "Active",
              value: stats.active,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
              color: "text-emerald-600 dark:text-emerald-400",
              bg: "bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/30",
              iconBg: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400",
            },
            {
              label: "Paused",
              value: stats.paused,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
              color: "text-[#F56948]",
              bg: "bg-[#F56948]/5 border border-[#F56948]/15",
              iconBg: "bg-[#F56948]/10 text-[#F56948]",
            },
            {
              label: "Pending Review",
              value: stats.pending,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
              color: "text-amber-600 dark:text-amber-400",
              bg: "bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/30",
              iconBg: "bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400",
            },
          ].map((stat) => (
            <div key={stat.label} className={`flex items-center gap-3 px-4 py-4 rounded-2xl ${stat.bg}`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${stat.iconBg}`}>
                {stat.icon}
              </div>
              <div>
                <p className={`text-xl font-extrabold leading-none ${stat.color}`}>{stat.value}</p>
                <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Divider ────────────────────────────────────────────────────── */}
        <div className="h-px bg-neutral-100 dark:bg-neutral-800" />

        {/* ── Toolbar: search + filters + sort + refresh ─────────────────── */}
        <div className="flex flex-col gap-4">

          {/* Row 1: search + sort + refresh */}
          <div className="flex items-center gap-3 flex-wrap">

            {/* Search input */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z" />
                </svg>
              </span>
              <input
                type="text"
                value={query}
                onChange={handleSearchChange}
                onKeyDown={(e) => e.key === "Enter" && query && handleSearch()}
                placeholder="Search by property name…"
                className="w-full pl-10 pr-10 py-2.5 text-sm rounded-xl border-2 border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-[#1A1A2E] dark:text-white placeholder-neutral-400 focus:outline-none focus:border-[#F56948] transition-colors duration-150"
              />
              {query && (
                <button
                  onClick={handleClear}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-[#F56948] transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Search button */}
            <button
              onClick={handleSearch}
              disabled={isSearchLoading || !query}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-[#F56948] text-white hover:bg-[#e0522f] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 shadow-sm shadow-[#F56948]/20 whitespace-nowrap"
            >
              {isSearchLoading ? (
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z" />
                </svg>
              )}
              {isSearchLoading ? "Searching…" : "Search"}
            </button>

            {/* Sort dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowSort((v) => !v)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border-2 border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-[#1A1A2E] dark:text-white hover:border-[#F56948]/40 transition-colors whitespace-nowrap"
              >
                <svg className="w-4 h-4 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M3 7h18M6 12h12M9 17h6" />
                </svg>
                {currentSortLabel}
                <svg className={`w-3.5 h-3.5 text-neutral-400 transition-transform ${showSort ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showSort && (
                <div className="absolute right-0 top-full mt-2 z-20 w-48 bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-xl shadow-xl overflow-hidden">
                  {SORT_OPTIONS.map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => { setSortKey(opt.key); setShowSort(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${
                        sortKey === opt.key
                          ? "bg-[#F56948]/10 text-[#F56948]"
                          : "text-[#1A1A2E] dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                      }`}
                    >
                      {opt.key === sortKey && <span className="mr-1.5">✓</span>}
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Refresh button */}
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              title="Refresh listings"
              className="flex items-center justify-center w-10 h-10 rounded-xl border-2 border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-400 hover:text-[#F56948] hover:border-[#F56948]/40 disabled:opacity-50 transition-all duration-150"
            >
              <svg className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>

            {/* Result count */}
            {!isEmpty && (
              <span className="text-xs text-neutral-400 dark:text-neutral-500 ml-auto whitespace-nowrap">
                Showing <span className="font-semibold text-[#1A1A2E] dark:text-white">{displayData.length}</span> of{" "}
                <span className="font-semibold text-[#1A1A2E] dark:text-white">{allData.length}</span> listings
              </span>
            )}
          </div>

          {/* Row 2: status filter pills */}
          <div className="flex items-center gap-2 flex-wrap">
            {STATUS_FILTERS.map((f) => {
              const count =
                f.key === "all" ? allData.length
                : f.key === "active" ? stats.active
                : f.key === "paused" ? stats.paused
                : f.key === "waiting_for_approval" ? stats.pending
                : allData.filter((i: any) => i.property_state === f.key).length;

              const isActive = statusFilter === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => setStatusFilter(f.key)}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all duration-150 ${
                    isActive
                      ? "bg-[#F56948] border-[#F56948] text-white shadow-sm"
                      : "bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:border-[#F56948]/40 hover:text-[#F56948]"
                  }`}
                >
                  {f.label}
                  <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold ${
                    isActive ? "bg-white/25 text-white" : "bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400"
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}

            {/* Active filter indicator */}
            {(statusFilter !== "all" || sortKey !== "default") && (
              <button
                onClick={() => { setStatusFilter("all"); setSortKey("default"); }}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold text-neutral-400 hover:text-[#F56948] transition-colors ml-1"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* ── Divider ────────────────────────────────────────────────────── */}
        <div className="h-px bg-neutral-100 dark:bg-neutral-800" />

        {/* ── Empty state ────────────────────────────────────────────────── */}
        {isEmpty ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#F56948]/10 flex items-center justify-center">
              <svg className="w-8 h-8 text-[#F56948]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 22V12h6v10" />
              </svg>
            </div>
            <div>
              <p className="text-base font-bold text-[#1A1A2E] dark:text-white">
                {query ? "No results found" : statusFilter !== "all" ? "No listings in this category" : "No properties yet"}
              </p>
              <p className="text-sm text-neutral-400 mt-1">
                {query
                  ? `No listings match "${query}". Try a different name.`
                  : statusFilter !== "all"
                  ? "Try switching the filter above to see other listings."
                  : "Add your first property to get started."}
              </p>
            </div>
            <div className="flex gap-3 mt-2 flex-wrap justify-center">
              {(query || statusFilter !== "all") && (
                <button
                  onClick={() => { handleClear(); setStatusFilter("all"); }}
                  className="px-5 py-2.5 rounded-xl text-sm font-semibold border-2 border-[#F56948]/30 text-[#F56948] hover:bg-[#F56948]/10 transition-colors"
                >
                  Clear filters
                </button>
              )}
              {allData.length === 0 && (
                <a
                  href="/add-listing-1"
                  className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-[#F56948] text-white hover:bg-[#e0522f] transition-colors shadow-sm"
                >
                  + Add your first listing
                </a>
              )}
            </div>
          </div>
        ) : (
          <SectionGridFeaturePlacesV4
            stayListings={displayData}
            setAllData={setAllData}
            query={query}
            setQuery={setQuery}
            fetchOldData={() => fetchData(page)}
            page={page}
            setPage={setPage}
            isLoading={isLoading}
          />
        )}

        {/* ── Pagination ─────────────────────────────────────────────────── */}
        {totalPages > 1 && !isEmpty && (
          <div className="flex justify-center pt-4">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}

      </div>
    </div>
  );
};

export default MyListing;